/* eslint-disable unicorn/no-null */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Affiliation } from '@generated/base-types';
import { SidePanelComponent } from '@new/admin/side-panel/side-panel.component';
import {
  ActionMenuComponent,
  ActionMenuOption
} from '@new/common/action-menu/action-menu.component';
import { InteractiveRowDirective } from '@new/common/directives/row-interaction.directive';
import { ModalService } from '@new/common/modal/modal.service';
import { AsyncNotNullPipe } from '@new/common/pipes/async-not-null.pipe';
import { CopyArrayPipe } from '@new/common/pipes/copy-array.pipe';
import { SearchInputComponent } from '@new/common/search-input/search-input.component';
import { FuzzySearchService } from '@new/common/services/fuzzy-search.service';
import { FormState } from '@new/common/types/form-types';
import {
  DragAndDropEvent,
  getDirectionFromIndices,
  getMoveQuantity
} from '@new/common/utils/drag-and-drop';
import { RemoteData } from '@new/common/utils/remote-data';
import { assertIsDefined } from '@new/common/utils/type-guards/is-defined';
import { TableWrapperComponent } from '@new/pools/pool/lib/components/man-table/man-table-wrapper/table-wrapper.component';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { TableModule } from 'primeng/table';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  pairwise,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';
import { CreateAffiliationMutationVariables } from '../../../services/create-affiliation.generated';
import { UpdateAffiliationMutationVariables } from '../../../services/update-affiliation.generated';
import {
  CreateAffiliation,
  DeleteAffiliation,
  SetAffiliationPosition,
  UpdateAffiliation
} from '../../../state/pool-details/affiliations/affiliations.actions';
import { AffiliationsState } from '../../../state/pool-details/affiliations/affiliations.state';
import { AffiliationFormComponent } from './affiliation-form/affiliation-form.component';

@Component({
  templateUrl: './affiliations-list.component.html',
  host: { class: 'page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SearchInputComponent,
    TableWrapperComponent,
    TranslateModule,
    TableModule,
    CommonModule,
    CopyArrayPipe,
    AsyncNotNullPipe,
    ActionMenuComponent,
    SidePanelComponent,
    AffiliationFormComponent,
    InteractiveRowDirective
  ]
})
export class AffiliationsListComponent implements OnDestroy {
  public readonly filterFields = ['id', 'name'];
  public sortBy = 'position';
  public inSearchMode = false;
  public readonly affiliations$: Observable<RemoteData<Affiliation[]>>;
  public readonly formState$: Observable<FormState<Partial<Affiliation>>>;
  public readonly menuOptions: ActionMenuOption[] = [
    {
      label: 'Edit',
      callback: (data: Affiliation) => this.onEdit(data)
    },
    {
      label: 'Delete',
      danger: true,
      callback: (data: Affiliation) => this.onDelete(data)
    }
  ];
  private searchStringSource = new BehaviorSubject<string>('');
  private searchString$ = this.searchStringSource.asObservable();
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private modalService: ModalService,
    private fuzzySearchService: FuzzySearchService<Affiliation>
  ) {
    this.formState$ = this.formState();
    this.dismissSidebarOnSuccessfulMutation();
    this.affiliations$ = combineLatest([
      this.store.select(AffiliationsState.affiliations),
      this.searchString$
    ]).pipe(
      map(([affiliations, searchString]) => {
        this.inSearchMode = searchString.length > 0;
        searchString.trim() === ''
          ? (this.sortBy = 'position')
          : (this.sortBy = '');

        const result = this.fuzzySearchService.search(
          affiliations.data || [],
          searchString,
          this.filterFields
        );

        return { ...affiliations, data: result };
      })
    );
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSearch(value: string): void {
    this.searchStringSource.next(value);
  }

  public onSearchClear(): void {
    this.searchStringSource.next('');
  }

  public onNew(): void {
    this.routeToForm('new');
  }

  public onEdit({ id }: Affiliation): void {
    this.routeToForm(id);
  }

  public onCreate({
    name
  }: Pick<CreateAffiliationMutationVariables, 'name'>): void {
    this.store.dispatch(new CreateAffiliation(name));
  }

  public onUpdate({ id, name }: UpdateAffiliationMutationVariables): void {
    this.store.dispatch(new UpdateAffiliation(id, name));
  }

  public onDismiss(): void {
    this.routeToForm();
  }

  public onReorder({ dragIndex, dropIndex }: DragAndDropEvent): void {
    if (this.inSearchMode) return;
    if (dragIndex === dropIndex) return;
    const { id } =
      this.store.selectSnapshot(AffiliationsState.findAffiliationAtIndex)(
        dragIndex
      ) ?? {};
    assertIsDefined(id);

    const moveDirection = getDirectionFromIndices(dragIndex, dropIndex);
    const moveQuantity = getMoveQuantity(dragIndex, dropIndex);

    this.store.dispatch(
      new SetAffiliationPosition(id, moveDirection, moveQuantity)
    );
  }

  public async onDelete(affiliation: Affiliation): Promise<void> {
    const confirmDelete = await this.modalService.confirmDelete();
    if (confirmDelete) {
      this.store.dispatch(new DeleteAffiliation(affiliation.id));
    }
  }

  private formState(): Observable<FormState<Partial<Affiliation>>> {
    const editingParam$ = this.route.queryParamMap.pipe(
      map(params => params.get('editing') ?? undefined)
    );
    const disabled$ = this.store
      .select(AffiliationsState.mutationRequestState)
      .pipe(
        map(requestState => requestState === 'loading'),
        distinctUntilChanged()
      );

    return combineLatest([editingParam$, disabled$]).pipe(
      switchMap(([editingParam, disabled]) => {
        const show: boolean = editingParam !== undefined;
        const id = show && editingParam !== 'new' ? editingParam : undefined;

        return this.affiliations$.pipe(
          filter(({ requestState }) => requestState === 'success'),
          take(1),
          map(({ data }) =>
            id === undefined ? undefined : data?.find(a => a.id === id)
          ),
          map(data => ({ show, disabled, data }))
        );
      })
    );
  }

  private dismissSidebarOnSuccessfulMutation(): void {
    this.store
      .select(AffiliationsState.mutationRequestState)
      .pipe(
        pairwise(),
        filter(
          ([lastState, currentState]) =>
            lastState === 'loading' && currentState === 'success'
        ),
        tap({ next: () => this.onDismiss() }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private routeToForm(editing?: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { editing },
      queryParamsHandling: 'merge'
    });
  }
}
